/**
 * File validation utilities for invoice import
 * Client-side validation before upload to backend
 */

/**
 * Allowed MIME types for invoice import
 * Backend supports: XML (KSeF), CSV, JSON, PDF, images (JPEG, PNG, TIFF)
 */
export const ALLOWED_MIME_TYPES = [
  // Images
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/tiff',
  'image/tif',
  // Documents
  'application/pdf',
  // Structured data
  'application/xml',
  'text/xml',
  'text/csv',
  'application/json',
] as const;

/**
 * Allowed file extensions (fallback if MIME type is missing/incorrect)
 */
export const ALLOWED_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.tiff',
  '.tif',
  '.pdf',
  '.xml',
  '.csv',
  '.json',
] as const;

/**
 * Maximum file size in bytes (10 MB)
 * Backend validation also enforces this limit
 */
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

/**
 * Maximum file size in human-readable format
 */
export const MAX_FILE_SIZE_MB = 10;

/**
 * File validation result
 */
export interface FileValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Validates file for invoice import
 * Checks: file size, MIME type, file extension
 *
 * @param file - File to validate
 * @returns Validation result with isValid flag and optional error message
 */
export function validateFile(file: File): FileValidationResult {
  // Check if file exists
  if (!file) {
    return {
      isValid: false,
      errorMessage: 'Plik nie został wybrany.',
    };
  }

  // Check file size (max 10 MB)
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      errorMessage: `Plik jest za duży. Maksymalny rozmiar to ${MAX_FILE_SIZE_MB} MB.`,
    };
  }

  // Check if file is empty
  if (file.size === 0) {
    return {
      isValid: false,
      errorMessage: 'Plik jest pusty.',
    };
  }

  // Check MIME type
  const isMimeTypeValid = ALLOWED_MIME_TYPES.includes(file.type as any);

  // Check file extension (fallback if MIME type is missing/incorrect)
  const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  const isExtensionValid = ALLOWED_EXTENSIONS.includes(fileExtension as any);

  if (!isMimeTypeValid && !isExtensionValid) {
    return {
      isValid: false,
      errorMessage:
        'Format pliku nie jest obsługiwany. Dozwolone formaty: JPEG, PNG, TIFF, PDF, XML, CSV, JSON.',
    };
  }

  // All checks passed
  return {
    isValid: true,
  };
}

/**
 * Formats file size to human-readable string
 *
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "2.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Gets file extension from filename
 *
 * @param filename - File name
 * @returns File extension with dot (e.g., ".pdf")
 */
export function getFileExtension(filename: string): string {
  return filename.substring(filename.lastIndexOf('.')).toLowerCase();
}

/**
 * Checks if file is an image based on MIME type
 *
 * @param file - File to check
 * @returns True if file is an image
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Checks if file is a PDF based on MIME type
 *
 * @param file - File to check
 * @returns True if file is a PDF
 */
export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf';
}

/**
 * Checks if file is structured data (XML, CSV, JSON)
 *
 * @param file - File to check
 * @returns True if file is structured data
 */
export function isStructuredDataFile(file: File): boolean {
  return (
    file.type === 'application/xml' ||
    file.type === 'text/xml' ||
    file.type === 'text/csv' ||
    file.type === 'application/json'
  );
}
